import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import TweenMax from 'TweenMax';
import { inject, observer } from 'mobx-react';
import throttle from 'lodash/throttle';

import Container from '../../Container';
import { FONT_SIZE_LARGE, MENU_ITEMS, TRANSITION_DURATION } from '../../../constants/index';
import {
  animated,
  crossScreenFontSize,
  crossScreenMarginBottom,
  toEm,
  toVw,
  toVwMobile,
  toVwTablet,
} from '../../../utils/styles';
import Footer from '../../Footer';
import Indent from '../../Indent';
import Links from '../../Links';
import SubscriptionForm from '../../SubscriptionForm';
import LinkFade from '../../LinkFade';
import mq from '@/utils/media-queries';
import LangSwitcher from './LangSwitcher';

const MARGIN_BOTTOM_COMMON = 172;
const TRANSITION_DELAY = 1000;

const Styled = styled.div`
  background-color: ${props => props.theme.colorBackground};
  overflow: hidden;
  overflow-y: auto;
  height: 0;
  -webkit-overflow-scrolling: touch;
  transition: background ${TRANSITION_DURATION} ${TRANSITION_DELAY}ms,
    color ${TRANSITION_DURATION} ${TRANSITION_DELAY}ms;
  width: 100vw;
`;

const Inner = styled.div`
  position: relative;
  padding-top: ${toVw(270)};
  padding-bottom: ${toVw(282)};

  ${mq.tablet(css`
    padding-top: ${toVwTablet(270)};
    padding-bottom: ${toVwTablet(287)};
  `)};

  ${mq.tablet(css`
    padding-top: ${toVwMobile(270)};
    padding-bottom: ${toVwMobile(257)};
  `)};
`;

const Items = styled.div`
  ${crossScreenMarginBottom(80)};
`;

const Item = styled.div`
  user-select: none;
`;

const ItemInnerLink = styled(LinkFade)`
  position: relative;
  color: ${props => props.theme.colorText};
  text-decoration: none;
  transition: color ${TRANSITION_DURATION} ${TRANSITION_DELAY}ms;
  font-size: ${toVw(FONT_SIZE_LARGE)};

  &:after {
    content: '';
    display: block;
    position: absolute;
    bottom: ${toEm(8, FONT_SIZE_LARGE)};
    height: ${toEm(4, FONT_SIZE_LARGE)};
    width: 100%;
    background-color: ${props => props.theme.colorText};
    opacity: 0;
    ${animated};
  }

  &:before {
    content: '';
    position: absolute;
    width: 100%;
    height: ${toEm(4, FONT_SIZE_LARGE)};
    left: 0;
    top: 58%;
    transform: translateY(-50%);
    transition: background-color 0.35s ease-out 2s;
  }

  &:hover {
    &:after {
      opacity: 1;
      transition: none;
    }
  }

  ${props =>
    props.active === 'true' &&
    css`
      color: ${props => props.theme.colorMinor};
      pointer-events: none;

      &:before {
        background-color: ${props => props.theme.colorMinor};
      }
    `};

  ${mq.tablet(css`
    font-size: ${toVwTablet(FONT_SIZE_LARGE)};
  `)};

  ${mq.mobile(css`
    font-size: ${toVwMobile(64)};
  `)};
`;

const MenuLinks = styled(Links)`
  margin-bottom: ${toVw(MARGIN_BOTTOM_COMMON)};

  ${mq.tablet(css`
    margin-bottom: ${toVwTablet(MARGIN_BOTTOM_COMMON)};
  `)};

  ${mq.mobile(css`
    margin-bottom: ${toVwMobile(MARGIN_BOTTOM_COMMON)};
  `)};
`;

class Menu extends React.PureComponent {
  static propTypes = {
    visible: PropTypes.bool.isRequired,
    isOnDark: PropTypes.bool,
    location: PropTypes.object.isRequired,
    onHide: PropTypes.func.isRequired,
    onShow: PropTypes.func.isRequired,
    scrollControllerStore: PropTypes.object.isRequired,
  };

  componentDidMount() {
    window.addEventListener('resize', this.resizeHandler);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.resizeHandler);
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.visible !== this.props.visible) {
      nextProps.visible ? this.animateShow() : this.animateHide();
    }
  }

  resizeHandler = throttle(() => {
    this.props.onHide();
  }, 200);

  keyPressHandler = e => {
    if (e.keyCode === 27) {
      this.props.onHide();
    }
  };

  animateShow() {
    this.props.scrollControllerStore.lock();
    // this.containerNode.style.height = '100vh';
    const height = window.innerHeight;
    this.containerNode.scrollTop = 0;
    TweenMax.fromTo(this.containerNode, 0.55, { height: 0 }, { height });
    TweenMax.staggerFromTo(this.itemsNode.childNodes, 0.35, { autoAlpha: 0 }, { autoAlpha: 1, delay: 0.3 }, 0.02);
    document.addEventListener('keydown', this.keyPressHandler);
  }

  animateHide() {
    this.props.scrollControllerStore.unlock();
    TweenMax.to(this.containerNode, 0.35, { height: 0, ease: Power1.easeIn });
    document.removeEventListener('keydown', this.keyPressHandler);
  }

  render() {
    const { pathname, search } = this.props.location;
    const pathnameModified = pathname[pathname.length - 1] === '/' ? pathname.slice(0, pathname.length - 1) : pathname;
    const currentPath = pathnameModified + search;

    return (
      <Styled data-test="menu" innerRef={node => (this.containerNode = node)} visible={this.props.visible}>
        <Inner>
          <Container>
            <Indent>
              <Items innerRef={node => (this.itemsNode = node)}>
                {MENU_ITEMS.map((item, index) => {
                  return (
                    <Item key={index}>
                      <ItemInnerLink
                        to={item.path}
                        data-test="menu-item"
                        active={item.path === currentPath ? 'true' : ''}
                      >
                        {item.name}
                      </ItemInnerLink>
                    </Item>
                  );
                })}
              </Items>
              <LangSwitcher />
              <MenuLinks />
              <SubscriptionForm />
            </Indent>
          </Container>
          <Footer />
        </Inner>
      </Styled>
    );
  }
}

export default inject(stores => ({
  menuStore: stores.store.menuStore,
  scrollControllerStore: stores.store.scrollControllerStore,
  themeStore: stores.store.theme,
}))(
  observer(({ menuStore, themeStore, ...props }) => (
    <Menu {...props} visible={menuStore.visible} themeIsDark={themeStore.isDark} />
  ))
);



// WEBPACK FOOTER //
// ./src/components/Header/components/Menu.js